import { Injectable } from '@angular/core';
import { clientIds } from '@msslib/constants';
import { BehaviorSubject, Observable, distinctUntilChanged, firstValueFrom, from, of, startWith, tap } from 'rxjs';
import { map, mergeWith } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { setUser as setSentryUser } from '../../../../../sentry';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { keys } from 'apps/clubhub/src/app/constants';
import { roles } from '../constants';
import { JwtHelperService } from './jwt-decode';
import {
  AuthenticationResultStatus,
  IAuthenticationResult,
  IUser,
} from '@msslib/models/authorization';
import { IAuthorizeService } from '@msslib/services/iauthorize.service';

/* eslint-disable-next-line spellcheck/spell-checker */
const authScope = 'openid profile criteria LenderHubApi MSS.IdentityServerAPI offline_access';

const authConfig: AuthConfig = {
  responseType: 'code',
  scope: authScope,
  strictDiscoveryDocumentValidation: false,
  disableAtHashCheck: true,
  useSilentRefresh: true,
  timeoutFactor: 0.75,
  sessionChecksEnabled: true,
  showDebugInformation: false,
  clearHashAfterLogin: true,
  silentRefreshShowIFrame: true,
};

@Injectable({
  providedIn: 'root',
})
export class AuthorizeAngularOidc2Service implements IAuthorizeService {
  public user: IUser | null;
  public historyView = false;
  public readonly allowCriteriaPlus = true;
  public readonly tokenKey = '';
  private readonly userStorageKey = 'currentUser';
  private readonly redirectUrlStorageKey = 'authReturnUrl';
  private readonly sessionAccessTokenKey = 'access_token';
  private readonly loginCallbackUrl = `${window.location.origin}/authentication/login-callback`;
  private readonly logoutCallbackUrl = `${window.location.origin}/authentication/logout-callback`;
  private readonly silentRefreshRedirectUri = `${window.location.origin}/silent-refresh.html`;

  private _isAgencyNumberRegistered: boolean | null = null;
  private userSubject = new BehaviorSubject<IUser | null>(null);
  private hasValidAccessToken$: Observable<boolean>;
  private isHttps = window.location.protocol === 'https:';

  public constructor(
    private oAuthService: OAuthService,
    private configService: ConfigService,
  ) {
    this.userSubject.subscribe(user => {
      setSentryUser(user ? {
        // Not adding email because it is PII, but IDs/client IDs are fine
        id: user.sub,
        externalClientId: user.sso_user,
        lenderId: user.lender,
      } : null);
    });

    this.configure();

    this.hasValidAccessToken$ = this.oAuthService.events.pipe(
      startWith(null),
      mergeWith(this.oAuthService.discoveryDocumentLoaded$, this.userSubject),
      map(() => this.oAuthService.hasValidAccessToken()),
    );
  }

  private configure() {
    const authConfiguration = {
      ...authConfig,
      clientId: this.configService.applicationName,
      issuer: this.configService.identityServerUrl,
      redirectUri: this.loginCallbackUrl,
      postLogoutRedirectUri: this.logoutCallbackUrl,
      silentRefreshRedirectUri: this.silentRefreshRedirectUri,
      useSilentRefresh: this.isHttps,
    } as AuthConfig;

    this.oAuthService.configure(authConfiguration);
  }

  public get isLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  public get tokenEndpoint() {
    return `${this.configService.config.identityServerUrl}/connect/token`;
  }

  public get isSsoUser$(): Observable<boolean | null> {
    return this.getUser().pipe(map((user: IUser) => user && user.sso_user === 'true'));
  }

  public getUserDistinct(): Observable<IUser | null> {
    return this.getUser().pipe(
      distinctUntilChanged((a, b) => a?.sub === b?.sub),
    );
  }

  public setUser(user: IUser, token: string | null = null): Observable<IUser | null> {
    return this.getExternalUser(token ?? '').pipe(
      tap(u => {
        if (u?.profile) {
          const externalUser = u.profile as IUser;
          this.setCurrentUser(externalUser);
        }
      }),
    );
  }

  public hasRole(role: string): boolean {
    return (
      this.user?.role &&
      (
        Array.isArray(this.user.role)
          ? this.user.role.some((x) => x === role)
          : this.user.role === role
      )
    ) ?? false;
  }

  public isAuthenticated(): Observable<boolean> {
    return this.hasValidAccessToken$;
  }

  public getAccessToken(): Observable<string | null> {
    return of(this.oAuthService.getAccessToken());
  }

  private cacheUser(user: IUser | null) {
    localStorage.setItem(this.userStorageKey, JSON.stringify(user));
    this.user = user;
  }

  private restoreCachedUser(): IUser | null {
    if (this.user) {
      return this.user;
    }
    const json = localStorage.getItem(this.userStorageKey);
    const cachedUser = json ? JSON.parse(json) : null;
    if (cachedUser) {
      this.setCurrentUser(cachedUser);
    }
    return this.user;
  }

  private async loadUserProfile(): Promise<IUser | null> {
    if (this.user) {
      return firstValueFrom(this.userSubject);
    }

    const userProfile = await this.oAuthService.loadUserProfile();
    if (!!userProfile && userProfile['info']) {
      const profile = userProfile['info'];
      const user = profile as IUser;
      this.setCurrentUser(user);
    }

    return Promise.resolve(null);
  }

  private setCurrentUser(user: IUser | null) {
    this.user = user;
    this.userSubject.next(user);
    this.cacheUser(user);
  }

  private get hasCurrentUser(): boolean {
    return !!this.user;
  }

  private async getCurrentUser(): Promise<IUser | null> {
    if (this.oAuthService.hasValidAccessToken()) {
      if (!this.hasCurrentUser) {
        const user = this.restoreCachedUser();
        if (!!user) {
          return Promise.resolve(user);
        }

        await this.loadUserProfile();
        return Promise.resolve(this.user);
      }

      return Promise.resolve(this.user);
    }

    return Promise.resolve(null);
  }

  // Previous implementation based on Observable
  public getUser(): Observable<IUser | null> {
    if (this.hasCurrentUser) {
      return this.userSubject;
    }

    from(this.getCurrentUser()).subscribe();
    return this.userSubject;
  }

  private error(message: string): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  private success(state: unknown): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  public async signOut(state: unknown): Promise<IAuthenticationResult> {
    this.oAuthService.logOut();
    return this.success(state);
  }

  public async completeSignOut(state: string): Promise<IAuthenticationResult> {
    this.setCurrentUser(null);
    sessionStorage.clear();
    return this.success(state);
  }

  public async signIn(state: any): Promise<IAuthenticationResult> {
    localStorage.setItem(this.redirectUrlStorageKey, JSON.stringify(state));
    this.oAuthService.initLoginFlow();

    return this.success(state);
  }

  public async completeSignIn(): Promise<IAuthenticationResult> {
    if (!this.oAuthService.hasValidAccessToken()) {
      this.oAuthService.initLoginFlow();
      return this.error('Login failed');
    }

    await this.loadUserProfile();
    const json = localStorage.getItem(this.redirectUrlStorageKey);
    const returnUrl = json ? JSON.parse(json) : null;

    localStorage.removeItem(this.redirectUrlStorageKey);
    return this.success(returnUrl);
  }

  public get isSsoUser(): boolean {
    return this.user?.sso_user === 'true';
  }

  public get isS365Client(): boolean {
    return this.user?.client_id === clientIds.smartr365;
  }

  public get clientId(): string | undefined {
    return this.user?.client_id;
  }

  public get agencyNumber() {
    const agencyNumber = parseInt(this.user?.agencyNumber ?? '');
    return isNaN(agencyNumber) ? null : agencyNumber;
  }

  public get getUserInformation() {
    return this.user;
  }


  public setAgencyNumber(newAgencyNumber: string | undefined, isRegistered: boolean | null = null) {
    if (!this.user) {
      return;
    }

    if (newAgencyNumber?.length) {
      this.user.agencyNumber = newAgencyNumber;
      sessionStorage.setItem(keys.agencyNumber, newAgencyNumber);
    } else {
      delete this.user.agencyNumber;
      sessionStorage.removeItem(keys.agencyNumber);
    }

    this._isAgencyNumberRegistered = isRegistered;
  }

  public get isAgencyNumberRegistered() {
    // All SSO users have the Claims role regardless of whether they have an agency number or whether that broker is
    // registered, so we just return the cached value. If the cached value is null, that means we need to ask the server
    // For registered users, they only get the Claims role if their assigned broker is registered.
    return this.isSsoUser
      ? this._isAgencyNumberRegistered
      : this.hasRole(roles.claims);
  }

  public set isAgencyNumberRegistered(value) {
    this._isAgencyNumberRegistered = this.isSsoUser ? value : null;
  }

  private getExternalUser(token: string): Observable<any> {
    if (token) {
      // Need to set external token to oAuth session
      sessionStorage.setItem(this.sessionAccessTokenKey, token);

      const service = new JwtHelperService();
      const profile = service.decodeToken(token);
      const agencyNumberInSession = sessionStorage.getItem(keys.agencyNumber);
      if (agencyNumberInSession && profile.sso_user === 'true') {
        profile.agencyNumber = agencyNumberInSession;
      }
      return of({ profile });
    }
    return of(null);
  }

  public async setupExternalLoginConfig(): Promise<void> {
    const config = {
      clientId: this.configService.applicationName,
      issuer: this.configService.identityServerUrl,

      responseType: 'client_credentials',
      scope: authScope,
      redirectUri: this.loginCallbackUrl,
      postLogoutRedirectUri: this.logoutCallbackUrl,
      strictDiscoveryDocumentValidation: false,
      disableAtHashCheck: true,
      timeoutFactor: 0.75,
      sessionChecksEnabled: true,
      clearHashAfterLogin: true,
    } as AuthConfig;

    this.oAuthService.configure(config);
    await this.oAuthService.loadDiscoveryDocument();
  }

  public async setupInternalLoginConfig(): Promise<void> {
    if (this.isHttps) {
      // Silent refresh requires the client to be served via HTTPS
      this.oAuthService.setupAutomaticSilentRefresh();
    }
    await this.oAuthService.loadDiscoveryDocumentAndTryLogin();
  }
}
